import { useEffect, useState } from "react";
import { Plus, Download, Barcode as BarcodeIcon, Power, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/lib/app-context";
import { inr, errMsg, dt } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, Field, NativeSelect, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StockQuickEdit, SyncBadge } from "@/components/StockQuickEdit";

const BLANK = {
  name: "", sku: "", barcode: "", alt_barcodes: [], plu_code: "", description: "", category_id: "", brand: "", aliases: [], images: [],
  purchase_price: 0, mrp: 0, online_price: 0, offline_price: 0, wholesale_price: null, promos: [], tax_rate: 5, hsn: "", zepio_tax_id: null,
  zepio_category_ids: [], unit: "pcs", is_loose: false, weight_unit: "", unit_weight: null, length: null, width: null, height: null,
  min_qty: null, max_qty: null, min_stock: 0, low_stock_threshold: 5, reorder_qty: 0, track_stock: true, active: true, sell_online: true,
  supplier_ids: [], price_change_reason: "",
};
const NUM = ["purchase_price", "mrp", "online_price", "offline_price", "wholesale_price", "tax_rate", "zepio_tax_id", "unit_weight", "length", "width", "height", "min_qty", "max_qty", "min_stock", "low_stock_threshold", "reorder_qty"];
const LIST = ["alt_barcodes", "aliases", "images", "zepio_category_ids"];

function ProductForm({ open, onClose, initial, onSaved }) {
  const { api, can } = useApp();
  const [f, setF] = useState(BLANK);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...BLANK, ...initial, price_change_reason: "" } : BLANK);
    api.get("/categories").then((r) => setCats(r.data));
  }, [open, initial, api]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const txt = (k, label, props = {}) => <Field label={label}><Input data-testid={`product-${k}-input`} value={LIST.includes(k) ? (f[k] || []).join(", ") : f[k] ?? ""} onChange={set(k)} {...props} /></Field>;
  const save = async () => {
    const body = { ...f };
    NUM.forEach((k) => { body[k] = body[k] === "" || body[k] === null || body[k] === undefined ? (["wholesale_price", "zepio_tax_id", "unit_weight", "length", "width", "height", "min_qty", "max_qty"].includes(k) ? null : 0) : Number(body[k]); });
    LIST.forEach((k) => { body[k] = Array.isArray(body[k]) ? body[k] : String(body[k] || "").split(",").map((x) => x.trim()).filter(Boolean); });
    body.zepio_category_ids = body.zepio_category_ids.map(Number);
    body.weight_unit = body.weight_unit || null;
    body.category_id = body.category_id || null;
    setSaving(true);
    try {
      const r = initial ? await api.put(`/products/${initial.id}`, body) : await api.post("/products", body);
      toast.success(initial ? "Product updated" : `Product ${r.data.erp_code} created`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const priceChanged = initial && ["purchase_price", "mrp", "online_price", "offline_price", "wholesale_price"].some((k) => Number(initial[k] || 0) !== Number(f[k] || 0));
  const promo = f.promos[0] || null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle>{initial ? `Edit ${initial.erp_code}` : "New product"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          {txt("name", "Product name *", { className: "" })}
          {txt("sku", "SKU")}
          {txt("barcode", "Barcode (manufacturer)")}
          <Field label="Category"><NativeSelect testid="product-category-select" value={f.category_id} onChange={set("category_id")} placeholder="—" options={cats.map((c) => ({ value: c.id, label: c.name }))} /></Field>
          {txt("brand", "Brand")}
          <Field label="Unit"><NativeSelect testid="product-unit-select" value={f.unit} onChange={set("unit")} options={["pcs", "pack", "box", "kg", "g", "l", "ml"].map((u) => ({ value: u, label: u }))} /></Field>
        </div>
        <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 p-3 mt-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-800 mb-2">Channel pricing (₹) — online and offline are independent</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {txt("purchase_price", "Purchase cost", { type: "number", step: "0.01", disabled: !can("products.edit") })}
            {txt("mrp", "MRP", { type: "number", step: "0.01" })}
            {txt("online_price", "Online price → Zepio", { type: "number", step: "0.01" })}
            {txt("offline_price", "Offline POS price", { type: "number", step: "0.01" })}
            {txt("wholesale_price", "Wholesale", { type: "number", step: "0.01" })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <Field label="Promo channel"><NativeSelect testid="promo-channel-select" value={promo?.channel || ""} placeholder="No promotion"
              onChange={(v) => setF({ ...f, promos: v ? [{ ...(promo || { price: f.offline_price, start: "", end: "" }), channel: v }] : [] })}
              options={[{ value: "both", label: "Both" }, { value: "online", label: "Online" }, { value: "offline", label: "Offline" }]} /></Field>
            {promo && <>
              <Field label="Promo price"><Input data-testid="promo-price-input" type="number" value={promo.price} onChange={(e) => setF({ ...f, promos: [{ ...promo, price: Number(e.target.value) }] })} /></Field>
              <Field label="From"><Input data-testid="promo-start-input" type="date" value={promo.start} onChange={(e) => setF({ ...f, promos: [{ ...promo, start: e.target.value }] })} /></Field>
              <Field label="To"><Input data-testid="promo-end-input" type="date" value={promo.end} onChange={(e) => setF({ ...f, promos: [{ ...promo, end: e.target.value }] })} /></Field>
            </>}
          </div>
          {priceChanged && <div className="mt-3">{txt("price_change_reason", "Reason for price change * (audited)")}</div>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
          {txt("tax_rate", "GST %", { type: "number" })}
          {txt("hsn", "HSN code")}
          {txt("zepio_tax_id", "Zepio tax ID", { type: "number" })}
          {txt("plu_code", "PLU (loose items)")}
          <Field label="Weight unit (Zepio)"><NativeSelect testid="product-weight-unit-select" value={f.weight_unit || ""} onChange={set("weight_unit")} placeholder="—" options={[{ value: "gm", label: "gm" }, { value: "kg", label: "kg" }]} /></Field>
          {txt("unit_weight", "Unit weight", { type: "number" })}
          {txt("length", "Length", { type: "number" })}
          {txt("width", "Width", { type: "number" })}
          {txt("height", "Height", { type: "number" })}
          {txt("min_qty", "Min order qty", { type: "number" })}
          {txt("max_qty", "Max order qty", { type: "number" })}
          {txt("min_stock", "Minimum stock", { type: "number" })}
          {txt("low_stock_threshold", "Low-stock threshold", { type: "number" })}
          {txt("reorder_qty", "Reorder qty", { type: "number" })}
          {txt("alt_barcodes", "Extra barcodes (comma)")}
          {txt("aliases", "Search aliases (comma)")}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {txt("images", "Image URLs (comma, in order)")}
          {txt("zepio_category_ids", "Zepio category IDs (comma)")}
          <Field label="Description" className="sm:col-span-2"><Textarea data-testid="product-description-input" value={f.description} onChange={set("description")} rows={2} /></Field>
        </div>
        <div className="flex flex-wrap gap-6 mt-2 text-sm">
          {[["track_stock", "Track stock"], ["is_loose", "Loose / sold by weight"], ["sell_online", "Sell online (Zepio)"], ["active", "Active in ERP"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2"><Switch data-testid={`product-${k}-switch`} checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: v })} />{l}</label>))}
        </div>
        <p className="text-xs text-muted-foreground">Disabling a product or turning off “Sell online” queues <code>is_active: false</code> for Zepio. Images, categories and aliases are never sent with price/stock sync — push them deliberately from the Zepio page.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="save-product-button">{saving ? "Saving…" : "Save product"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductDetail({ id, onClose }) {
  const { api } = useApp();
  const [p, setP] = useState(null);
  useEffect(() => {
    if (id) api.get(`/products/${id}`).then((r) => setP(r.data));
    else setP(null);
  }, [id, api]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-dialog">
        <DialogHeader><DialogTitle>{p?.name}</DialogTitle></DialogHeader>
        {p && <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[["ERP ID", p.erp_code], ["Barcode", p.barcode || "-"], ["Available", p.stock?.available ?? 0], ["Racks", (p.stock?.racks || []).join(", ") || "-"]].map(([a, b]) => <div key={a}><div className="text-xs text-muted-foreground">{a}</div><div className="font-mono">{b}</div></div>)}
          </div>
          <div><h4 className="font-semibold mb-1">Price history</h4>{p.price_history.length ? p.price_history.map((h) => (
            <div key={h.id} className="border-t py-1.5 text-xs"><b>{dt(h.at)}</b> · {h.user} · {h.reason} — {Object.entries(h.changes).map(([k, v]) => `${k}: ${JSON.stringify(v.from)} → ${JSON.stringify(v.to)}`).join("; ")}</div>)) : <Empty text="No price changes" />}</div>
          <div><h4 className="font-semibold mb-1">Purchase history</h4>{p.purchase_history.length ? p.purchase_history.map((h, i) => (
            <div key={i} className="border-t py-1.5 text-xs">{dt(h.created_at)} · {h.number} · {h.supplier_name} · {h.qty} @ {inr(h.cost)} · batch {h.batch_no || "-"}</div>)) : <Empty text="No purchases yet" />}</div>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const { api, branchId, can } = useApp();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [qe, setQe] = useState(null);
  const [cats, setCats] = useState({});
  useEffect(() => {
    api.get("/categories").then((r) => setCats(Object.fromEntries(r.data.map((c) => [c.id, c]))));
  }, [api]);
  const load = () => api.get("/products", { params: { q, status, branch_id: branchId } }).then((r) => setRows(r.data));
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, status, branchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (p) => {
    try {
      await api.post(`/products/${p.id}/active`, { active: !p.active, reason: "Toggled from product list" });
      toast.success(p.active ? "Disabled — Zepio will be updated to inactive" : "Enabled");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const genBarcode = async (p) => {
    try { const r = await api.post(`/products/${p.id}/generate-barcode`); toast.success(`Barcode ${r.data.barcode} assigned`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportCsv = () => {
    const head = ["erp_code", "name", "sku", "barcode", "unit", "purchase_price", "mrp", "online_price", "offline_price", "tax_rate", "available", "racks", "active"];
    const lines = rows.map((r) => [r.erp_code, r.name, r.sku, r.barcode, r.unit, r.purchase_price, r.mrp, r.online_price, r.offline_price, r.tax_rate, r.stock.available, r.stock.racks.join(" "), r.active].map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(","));
    const url = URL.createObjectURL(new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: "products.csv" }).click();
  };
  return (
    <div>
      <PageHeader title="Products" subtitle="Product master with independent online and offline prices">
        <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button"><Download className="h-4 w-4 mr-1" />CSV</Button>
        {can("products.edit") && <Button onClick={() => { setEdit(null); setFormOpen(true); }} data-testid="add-product-button"><Plus className="h-4 w-4 mr-1" />Add product</Button>}
      </PageHeader>
      <Panel>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input data-testid="product-search-input" placeholder="Search name, SKU, barcode, ERP ID, alias" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm" />
          <NativeSelect testid="product-status-filter" value={status} onChange={setStatus} className="sm:w-40" placeholder="All statuses" options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Disabled" }]} />
        </div>
        {rows.length ? (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="py-2 w-12"></th><th>Product</th><th>Category</th><th>Barcode</th><th>Cost</th><th>MRP</th><th>Online</th><th>POS</th><th>GST</th><th>Available</th><th>Racks</th><th>Status</th><th>Website sync</th><th></th></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`product-row-${p.erp_code}`}>
                <td className="py-2">{p.images?.[0] ? <img src={p.images[0]} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="h-10 w-10 rounded-lg object-cover border bg-emerald-50" /> : <div className="h-10 w-10 rounded-lg bg-emerald-50" />}</td>
                <td className="py-2"><button className="font-semibold hover:underline text-left" onClick={() => setDetail(p.id)}>{p.name}</button><div className="font-mono text-xs text-muted-foreground">{p.erp_code} {p.sku && `· ${p.sku}`}</div></td>
                <td className="text-xs">{(() => { const c = cats[p.category_id]; const par = c && cats[c.parent_id]; return c ? (par ? <>{par.name}<div className="text-muted-foreground">› {c.name}</div></> : c.name) : "-"; })()}</td>
                <td className="font-mono text-xs">{p.barcode || (can("products.edit") ? <Button size="sm" variant="ghost" onClick={() => genBarcode(p)} data-testid={`generate-barcode-${p.erp_code}`}><BarcodeIcon className="h-3 w-3 mr-1" />Generate</Button> : "-")}</td>
                <td>{inr(p.purchase_price)}</td><td>{inr(p.mrp)}</td>
                <td className="text-blue-700 font-medium">{inr(p.effective_online_price)}</td><td className="text-emerald-700 font-medium">{inr(p.effective_offline_price)}</td>
                <td>{p.tax_rate}%</td>
                <td className={p.stock.available <= (p.low_stock_threshold || 0) ? "text-amber-600 font-semibold" : ""}>{p.stock.available} {p.unit}</td>
                <td className="font-mono text-xs">{p.stock.racks.join(", ") || "-"}</td>
                <td><StatusBadge s={p.active ? "active" : "disabled"} />{!p.sell_online && <div className="text-[10px] text-muted-foreground">offline only</div>}</td>
                <td><SyncBadge sync={p.sync} /></td>
                <td className="whitespace-nowrap"><Button size="sm" variant="outline" onClick={() => setQe(p.id)} data-testid={`stock-edit-${p.erp_code}`}>Stock</Button>{can("products.edit") && <>
                  <Button size="icon" variant="ghost" onClick={() => { setEdit(p); setFormOpen(true); }} data-testid={`edit-product-${p.erp_code}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => toggle(p)} data-testid={`toggle-product-${p.erp_code}`}><Power className={`h-4 w-4 ${p.active ? "text-emerald-600" : "text-red-500"}`} /></Button></>}</td>
              </tr>))}</tbody></table></div>
        ) : <Empty text="No products yet. Add your first product." />}
      </Panel>
      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} initial={edit} onSaved={load} />
      <ProductDetail id={detail} onClose={() => setDetail(null)} />
      <StockQuickEdit productId={qe} onClose={() => setQe(null)} onChanged={load} />
    </div>
  );
}
